<template>
  <div
    class="min-h-screen flex flex-col items-center justify-center bg-cover bg-center relative" 
    :style="{ backgroundImage: 'url(' + backgroundImage + ')' }"
  >
    <!-- Semi-transparent overlay -->
    <div class="absolute inset-0 bg-primary-dark/60 backdrop-blur-sm"></div>
    
    <!-- Content container -->
    <div class="max-w-md w-full space-y-8 bg-white/90 dark:bg-surface-dark/90 backdrop-blur-md p-10 rounded-2xl shadow-2xl relative z-10 border border-white/20">
      <!-- Logo -->
      <div class="flex justify-center">
        <img 
          src="@/assets/mivu_logo.png" 
          alt="MIVU - Observability Your Way" 
          class="h-16 w-auto dark:hidden" 
        />
        <img 
          src="@/assets/mivu_logo_white.png" 
          alt="MIVU - Observability Your Way" 
          class="h-16 w-auto hidden dark:block" 
        />
      </div>
      
      <div>
        <h1 class="mt-6 text-center text-3xl font-extrabold text-primary dark:text-white tracking-tight">
          Welcome Back
        </h1>
        <p class="mt-2 text-center text-sm text-secondary dark:text-gray-400">
          Sign in to your account to continue
        </p>
      </div>
      
      <form @submit.prevent="login" class="mt-8 space-y-6">
        <div
          v-if="errorMessage"
          class="mb-4 p-4 rounded-lg bg-danger/10 text-danger border border-danger/20 text-sm font-medium flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          {{ errorMessage }}
        </div>
        <div class="rounded-md shadow-sm space-y-4">
          <div>
            <label for="username" class="sr-only">Username</label>
            <input
              id="username"
              type="text"
              v-model="username"
              required
              class="appearance-none relative block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary focus:z-10 sm:text-sm transition-all duration-200 bg-white dark:bg-gray-800"
              placeholder="Username"
            />
          </div>
          <div class="relative">
            <label for="password" class="sr-only">Password</label>
            <input
              id="password"
              :type="showPassword ? 'text' : 'password'"
              v-model="password"
              required
              @keyup="checkCapsLock"
              class="appearance-none relative block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary focus:z-10 sm:text-sm transition-all duration-200 bg-white dark:bg-gray-800"
              placeholder="Password"
            />
            <div
              v-show="capsLockOn"
              class="absolute top-0 right-0 px-2 py-1 bg-warning text-white text-xs rounded-bl-md rounded-tr-md"
            >
              Caps lock is on
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div class="text-sm">
            <router-link
              to="/password_reset"
              class="font-medium text-primary dark:text-white hover:text-primary-light transition-colors"
            >
              Forgot password?
            </router-link>
          </div>
          <div class="text-sm">
            <button
              @click="toggleShowPassword"
              type="button"
              class="font-medium text-white bg-primary hover:bg-primary-light transition-colors focus:outline-none"
            >
              {{ showPassword ? "Hide password" : "Show password" }}
            </button>
          </div>
        </div>

        <div>
          <button
            type="submit"
            class="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            Sign in
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { login } from "@/services/authService";
import { useStore } from "@/store";
import backgroundImage from "@/assets/background.png";

export default {
  data() {
    return {
      username: "",
      password: "",
      showPassword: false,
      capsLockOn: false,
      errorMessage: "",
      backgroundImage
    };
  },
  methods: {
    async login() {
      this.errorMessage = ""; // Clear any previous error
      try {
        await login(this.username, this.password);
        await useStore.loadUser(); // Use store object directly without calling it as a function
        const redirectPath = localStorage.getItem('redirectAfterLogin');
        if (redirectPath) {
          localStorage.removeItem('redirectAfterLogin');
          this.$router.replace(redirectPath);
        } else{
          this.$router.push("/");
        }
      } catch (error) {
        this.errorMessage =
          error.message ||
          "Login failed. Please check your credentials and try again.";
      }
    },
    checkCapsLock(event) {
      this.capsLockOn =
        event.getModifierState && event.getModifierState("CapsLock");
    },
    toggleShowPassword() {
      this.showPassword = !this.showPassword;
    },
  },
};
</script>
